import { notifications } from '@mantine/notifications';
import i18n from '@/i18n';
import { OrderFormData, OrderFormErrors } from '../components/OrderForm';
import { hasAuthorizedPerson } from './common-helpers';

// Re-export for backwards compatibility
export { isValidOrderCode } from './order-code-validation';

// Type-safe translation keys for order validation
const VALIDATION_KEYS = {
  patientRequired: 'validation.patientRequired',
  requesterRequired: 'validation.requesterRequired',
  testCodeRequired: 'validation.testCodeRequired',
  sampleTypeRequired: 'validation.sampleTypeRequired',
  answerDeliveryRequired: 'validation.answerDeliveryRequired',
  authorizationFormRequired: 'validation.authorizationFormRequired',
  authorizedPersonRequired: 'validation.authorizedPersonRequired',
  error: 'validation.error',
  completeRequiredFields: 'validation.completeRequiredFields',
} as const;

type ValidationKey = keyof typeof VALIDATION_KEYS;

export function validateOrderForm(formData: OrderFormData): {
  isValid: boolean;
  errors: OrderFormErrors;
} {
  const errors: OrderFormErrors = {};

  // Type-safe translation helper
  const t = (key: ValidationKey): string => {
    return i18n.t(`lims:${VALIDATION_KEYS[key]}`, {
      defaultValue: VALIDATION_KEYS[key],
    });
  };

  if (!formData.patient) {
    errors.patient = t('patientRequired');
  }

  if (!formData.requester) {
    errors.requester = t('requesterRequired');
  }

  if (!formData.testCode) {
    errors.testCode = t('testCodeRequired');
  }

  if (!formData.sampleType) {
    errors.sampleType = t('sampleTypeRequired');
  }

  if (!formData.answerDelivery || formData.answerDelivery.length === 0) {
    errors.answerDelivery = t('answerDeliveryRequired');
  }

  if (
    hasAuthorizedPerson(formData.answerDelivery) &&
    !formData.authorizationFormFile
  ) {
    errors.authorizationForm = t('authorizationFormRequired');
  }

  if (
    hasAuthorizedPerson(formData.answerDelivery) &&
    !formData.authorizedPerson?.trim()
  ) {
    errors.authorizedPerson = t('authorizedPersonRequired');
  }

  const isValid = Object.keys(errors).length === 0;

  if (!isValid) {
    notifications.show({
      title: t('error'),
      message: t('completeRequiredFields'),
      color: 'red',
    });
  }

  return { isValid, errors };
}
